import axios from 'axios';
import Cookies from 'js-cookie';
import TokenService from './tokenservices/token.service';
import { API_BASE_URL, TOKEN_REFRESH } from './api_routes';
import { setAccessToken } from '../redux/usersSlice';
import { buildLoginRedirectUrlFromWindow } from '../utils/postAuthRedirect';
import jwt_decode from 'jwt-decode';
import dayjs from 'dayjs';
const baseUrl = API_BASE_URL;
let store;

axios.defaults.withCredentials = true;
axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN';
axios.defaults.xsrfCookieName = 'csrftoken';

export const injectStore = _store => {
    store = _store;
};

let refreshInFlight = null;
let authRedirectStarted = false;

const isAuthFailure = err => {
    const status = err?.response?.status;
    if (status === 401 || status === 403) return true;
    // jwt-decode throws this when the stored access token is not a JWT.
    return err?.name === 'InvalidTokenError';
};

const clearRefreshCookie = () => {
    Cookies.remove('refresh', { path: '/' });
    Cookies.remove('refresh', { path: '/', secure: true, sameSite: 'strict' });
};

const redirectToLoginOnce = () => {
    if (authRedirectStarted || typeof window === 'undefined') return;
    authRedirectStarted = true;
    clearRefreshCookie();
    try {
        store?.dispatch(setAccessToken(''));
    } catch (_) {
        // still leave the page
    }
    window.location.replace(buildLoginRedirectUrlFromWindow());
};

const refreshAccessToken = () => {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            const refreshToken = Cookies.get('refresh');
            if (!refreshToken) {
                const missing = new Error('missing refresh token');
                missing.response = { status: 401 };
                throw missing;
            }
            const response = await axios.post(`${API_BASE_URL}/${TOKEN_REFRESH}`, {
                refresh: refreshToken,
            });
            const access = response?.data?.access;
            if (!access) {
                const invalid = new Error('refresh response missing access token');
                invalid.response = { status: 401 };
                throw invalid;
            }
            store.dispatch(setAccessToken(access));
            localStorage.setItem('authTokens', JSON.stringify(response.data));
            const nextRefresh = response.data.refresh || refreshToken;
            if (nextRefresh) {
                Cookies.set('refresh', nextRefresh, {
                    secure: true,
                    sameSite: 'strict',
                });
            }
            return access;
        })().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

const axiosInstance = axios.create({
    baseURL: baseUrl,
    timeout: 900000,
    headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
    },
});

// axiosInstance.interceptors.request.use(
//     config => {
//         const token = store?.getState()?.user?.access;
//         if (token) {
//             config.headers = {
//                 Authorization: `Bearer ${token}`,
//             };
//         }
//         return config;
//     },
//     error => {
//         return Promise.reject(error);
//     },
// );
axiosInstance.interceptors.request.use(async req => {
    const token = store?.getState()?.user?.access;
    if (token) {
        req.headers = {
            Authorization: `Bearer ${token}`,
        };

        try {
            const user = jwt_decode(token);
            const isExpired = dayjs.unix(user.exp).diff(dayjs()) < 1;
            if (!isExpired) return req;

            // #region agent log
            if (String(req.url || '').includes('tent')) {
                fetch('http://127.0.0.1:7798/ingest/539ad776-defd-4048-9a51-bd9937645f01', {
                    method: 'POST',
                    keepalive: true,
                    headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '9d55d5' },
                    body: JSON.stringify({
                        sessionId: '9d55d5',
                        runId: 'pre-fix',
                        hypothesisId: 'H2',
                        location: 'axios.js:refresh',
                        message: 'tent request refreshing token',
                        data: { url: String(req.url || '').slice(0, 180) },
                        timestamp: Date.now(),
                    }),
                }).catch(() => {});
            }
            // #endregion

            const access = await refreshAccessToken();
            req.headers.Authorization = `Bearer ${access}`;
        } catch (err) {
            // #region agent log
            fetch('http://127.0.0.1:7798/ingest/539ad776-defd-4048-9a51-bd9937645f01', {
                method: 'POST',
                keepalive: true,
                headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '9d55d5' },
                body: JSON.stringify({
                    sessionId: '9d55d5',
                    runId: 'post-fix',
                    hypothesisId: 'H4',
                    location: 'axios.js:refreshFail',
                    message: 'token refresh failed',
                    data: {
                        url: String(req.url || '').slice(0, 180),
                        status: err?.response?.status || null,
                        error: String(err?.message || 'refresh failed').slice(0, 200),
                        authFailure: isAuthFailure(err),
                    },
                    timestamp: Date.now(),
                }),
            }).catch(() => {});
            // #endregion
            // A wake-from-sleep network blip must not tear the page down.
            // Only a rejected refresh token sends the user to login, and the
            // original request must not go out with the expired access token.
            if (isAuthFailure(err)) {
                redirectToLoginOnce();
            }
            return Promise.reject(err);
        }
    }

    return req;
});

export const resetAuthSessionForTests = () => {
    refreshInFlight = null;
    authRedirectStarted = false;
};

export default axiosInstance;
